import React from 'react';
import { type BibleViewOptions, type BiblePassageContent, quickID } from '@soulhx/fs-common';
import { BibleContentItem } from './BibleContentItem.js';
import { contentParaTypes } from '../../../helpers/constants.js';

/**
 * Strips verse ones that come directly after a chapter, in document order.
 *
 * A chapter and its "verse 1" are often not siblings in the content tree - e.g.
 * a chapter tag is immediately followed by a paragraph, and verse 1 is the
 * paragraph's first item. So this walks the whole tree in document order
 * (depth-first), threading a single "did we just see a chapter?" flag through
 * both siblings and nested children, treating wrapper tags (like paragraphs)
 * as transparent. Any other actual, non-blank content resets the flag - a
 * whitespace-only text node (a stray formatting artifact, not something a
 * reader would see) is treated as transparent too.
 *
 * Needs to happen *after* other content is filtered (e.g., footnotes,
 * cross-refs), because that impacts how this should work.
 *
 * - [Chapter 3] [Verse 1]: should have the verse stripped
 * - [Chapter 3] [Para [Verse 1]]: should have the verse stripped
 * - [Chapter 3] [Footnote] [Verse 1]: should not have the verse stripped
 * - [Chapter 3] [" "] [Verse 1]: should have the verse stripped (blank text is invisible)
 *
 * So if footnotes are being pulled out, then this function should remove the verse, as well.
 *
 * @param content Already filtered content
 * @returns Content with extraneous "verse 1" indicators removed
 */
const stripVerseOnes = (content: BiblePassageContent[]) => {
  const state = { justSawChapter: false };

  const walk = (items: BiblePassageContent[]): BiblePassageContent[] => {
    return items.reduce<BiblePassageContent[]>((result, item) => {
      if (item.name === 'verse' && state.justSawChapter) {
        state.justSawChapter = false;
        return result;
      }

      if (item.name === 'chapter') {
        result.push(item.items ? { ...item, items: walk(item.items) } : item);
        state.justSawChapter = true;
        return result;
      }

      if (item.items) {
        result.push({ ...item, items: walk(item.items) });
      } else {
        result.push(item);
        if (item.type !== 'text' || (item.text ?? '').trim().length > 0) state.justSawChapter = false;
      }

      return result;
    }, []);
  };

  return walk(content);
};

const filterContentTree = (content: BiblePassageContent[], options: BibleViewOptions): BiblePassageContent[] => {
  return content.reduce<BiblePassageContent[]>((result, item) => {
    if (!options.verseNums && (item.name === 'verse' || item.name === 'chapter')) return result;

    if (
      item.type === 'tag' &&
      item.name === 'para' &&
      !options.headings &&
      item.attrs?.style &&
      !contentParaTypes.includes(item.attrs.style)
    ) {
      return result;
    }

    result.push(item.items ? { ...item, items: filterContentTree(item.items, options) } : item);

    return result;
  }, []);
};

export const filterContent = (content: BiblePassageContent[], options: BibleViewOptions): BiblePassageContent[] => {
  if (options.crossRef && options.footnotes && options.headings && options.redLetters && options.verseNums) {
    return stripVerseOnes(content);
  }

  return stripVerseOnes(filterContentTree(content, options));
};

export const generateItemMap = (options: BibleViewOptions, filterStyle?: string) => {
  const mapFunc = (item: BiblePassageContent) => {
    return <BibleContentItem key={quickID()} filterStyle={filterStyle} item={item} options={options} />;
  };

  return mapFunc;
};
